import { Link } from "react-router-dom";
import { AlertTriangle, BatteryLow, Bluetooth, CheckCircle2, Cpu, Gamepad2, Lightbulb, Loader2, Unplug, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useHub } from "@/hub/HubContext";
import { describeFirmware } from "@/hub/firmware";

const Row = ({ icon: Icon, label, value, testId, tone = "" }) => (
  <div className="flex items-center justify-between gap-3 py-2 text-sm">
    <span className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" />{label}</span>
    <span className={`text-right font-medium ${tone}`} data-testid={testId}>{value}</span>
  </div>
);

const Notice = ({ tone = "info", children, testId }) => (
  <div
    data-testid={testId}
    className={`flex gap-2.5 rounded-xl border p-3 text-sm ${tone === "warn" ? "border-accent/40 bg-[hsl(var(--accent-soft))]" : "bg-muted/60"}`}
  >
    {tone === "warn" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent dark:text-primary" /> : <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
    <div className="leading-relaxed">{children}</div>
  </div>
);

const LIGHTS = [
  { c: "bg-orange-500 blink", t: "Blinking orange — searching for the controller" },
  { c: "bg-emerald-500", t: "Green — controller connected" },
];

export const HubPanel = ({ onConnect, onIdentify, identifying }) => {
  const hub = useHub();
  const fw = describeFirmware(hub.firmware);
  const connected = hub.status === "connected";
  const inFrame = typeof window !== "undefined" && window.self !== window.top;

  return (
    <div className="space-y-4 p-4" data-testid="hub-panel">
      {!hub.supported && (
        <Notice tone="warn" testId="bluetooth-unsupported-alert">
          This browser can't talk to hubs over Bluetooth. Use <b>Chrome</b> or <b>Edge</b> on a computer, Chromebook or Android tablet. Safari (iPad/iPhone) and Firefox don't support it. <Link to="/compatibility#browsers" className="font-semibold underline" data-testid="hub-panel-compat-link">See what works</Link>
        </Notice>
      )}
      {hub.supported && inFrame && (
        <Notice testId="iframe-bluetooth-hint">If the hub list never opens, open HubPilot in its own browser tab — some embedded previews block Bluetooth.</Notice>
      )}

      <div className="rounded-2xl border bg-card p-4">
        <div className="flex items-center gap-3">
          <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${connected ? "bg-primary text-primary-foreground pulse-ring" : "bg-muted text-muted-foreground"}`}>
            <Cpu className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-bold" data-testid="hub-name">{connected || hub.status === "reconnecting" ? hub.name : "No hub connected"}</p>
            <p className="text-xs capitalize text-muted-foreground" data-testid="hub-connection-state">{hub.status}</p>
          </div>
        </div>

        {(connected || hub.status === "reconnecting") && (
          <div className="mt-3 divide-y border-t">
            <Row icon={Zap} label="Firmware" value={`${fw.label} ${fw.version}`} testId="hub-firmware-version" />
            <Row icon={Gamepad2} label="Xbox pairing" testId="hub-xbox-support"
              value={fw.unsupported ? "Reinstall needed" : fw.xbox === false ? "Update needed" : fw.xbox ? "Supported" : "Unknown"}
              tone={fw.unsupported || fw.xbox === false ? "text-destructive" : fw.xbox ? "text-[hsl(var(--success))]" : ""} />
            <Row icon={CheckCircle2} label="Robot" value={hub.running ? "Running" : "Stopped"} testId="hub-program-state" />
            {hub.lowBattery && <Row icon={BatteryLow} label="Battery" value="Low — charge soon" tone="text-destructive" testId="hub-low-battery" />}
          </div>
        )}

        {hub.progress !== null && (
          <div className="mt-3 space-y-1.5" data-testid="download-progress">
            <p className="text-xs text-muted-foreground">Sending setup… {Math.round(hub.progress * 100)}%</p>
            <Progress value={hub.progress * 100} />
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          {connected ? (
            <Button variant="outline" className="rounded-full" onClick={hub.disconnect} data-testid="hub-disconnect-button">
              <Unplug className="mr-1.5 h-4 w-4" />Disconnect
            </Button>
          ) : (
            <Button className="rounded-full" onClick={onConnect} disabled={!hub.supported || hub.status !== "disconnected"} data-testid="hub-panel-connect-button">
              <Bluetooth className="mr-1.5 h-4 w-4" />Connect hub
            </Button>
          )}
          <Button variant="secondary" className="rounded-full" onClick={onIdentify} disabled={!connected || identifying || hub.progress !== null} data-testid="identify-hub-button">
            {identifying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Lightbulb className="mr-1.5 h-4 w-4" />}Identify
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Identify flashes and beeps the hub you're connected to, so you know it's yours. It replaces the setup on the hub until you press Drive again.</p>
      </div>

      {connected && fw.unsupported && (
        <Notice tone="warn" testId="unsupported-firmware-notice">
          This hub has HubPilot firmware, which is no longer supported. <Link className="font-semibold underline" to="/firmware">Install the official Pybricks firmware</Link> to drive. It takes about five minutes.
        </Notice>
      )}
      {connected && !fw.unsupported && fw.xbox === false && (
        <Notice tone="warn" testId="firmware-too-old-notice">
          This firmware is too old to pair an Xbox controller. <Link className="font-semibold underline" to="/firmware">Install the latest Pybricks firmware</Link>.
        </Notice>
      )}

      <div className="rounded-2xl border bg-card p-4">
        <p className="flex items-center gap-2 font-display font-bold"><Gamepad2 className="h-4 w-4 text-primary" />Pair your Xbox controller</p>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
          <li>Press <b className="text-foreground">Drive</b>. The hub starts searching for a controller.</li>
          <li>First time only: hold the small <b className="text-foreground">pair button</b> on top of the controller until the Xbox logo flashes fast.</li>
          <li>After that, just switch the controller on — it remembers this hub.</li>
          <li>You can close the laptop now. Press the hub's centre button to start driving again.</li>
        </ol>
        <div className="mt-3 space-y-1.5">
          {LIGHTS.map((l) => (
            <p key={l.t} className="flex items-center gap-2 text-xs text-muted-foreground"><span className={`h-2.5 w-2.5 rounded-full ${l.c}`} />{l.t}</p>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground"><b className="text-foreground">Failsafe:</b> if the controller drops out, every motor stops and the hub reconnects on its own.</p>
      </div>
    </div>
  );
};
